// src/components/sops/SopFolderDialog.js — sopweb-bdb7afcd (SOPs Library, Seam 2)
// New folder / Rename / Move / Archive / Restore a folder. The server checks every one
// (5 levels deep at most, not inside itself, archive only when empty) and its words are shown as-is.
import React, { useMemo, useState } from 'react';
import { Button, FieldLabel, Modal, Notice, inputClass } from './SopUi';
import { createFolder, renameFolder, moveFolder, archiveFolder, friendlyError } from './sopsApi';
import { LIMITS, newFolderParents, moveFolderTargets } from '../../utils/sopsUi';

const TITLES = { new: 'New folder', rename: 'Rename folder', move: 'Move folder', archive: 'Archive folder', restore: 'Restore folder' };
const indent = (depth) => `${'   '.repeat(Math.max(0, depth - 1))}${depth > 0 ? '└ ' : ''}`;

export default function SopFolderDialog({ mode, folder, folders, defaultParentId, onClose, onDone }) {
    const [name, setName] = useState(mode === 'rename' ? folder?.name || '' : '');
    const parents = useMemo(() => (mode === 'new' ? newFolderParents(folders) : mode === 'move' ? moveFolderTargets(folders, folder?.id) : []), [mode, folders, folder]);
    const startParent = mode === 'new' && parents.some((p) => p.id === (defaultParentId || null)) ? (defaultParentId || null) : (parents[0] ? parents[0].id : null);
    const [parentId, setParentId] = useState(startParent);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const needsName = mode === 'new' || mode === 'rename';
    const problem = needsName && !name.trim() ? 'Give the folder a name.' : needsName && name.trim().length > LIMITS.FOLDER_NAME_MAX ? `Folder names can be up to ${LIMITS.FOLDER_NAME_MAX} characters.` : mode === 'move' && parents.length === 0 ? 'There is nowhere else this folder can go.' : '';

    const save = async () => {
        if (problem) { setError(problem); return; }
        setBusy(true); setError('');
        try {
            if (mode === 'new') await createFolder(name.trim(), parentId);
            else if (mode === 'rename') await renameFolder(folder.id, name.trim());
            else if (mode === 'move') await moveFolder(folder.id, parentId);
            else if (mode === 'archive') await archiveFolder(folder.id, true);
            else if (mode === 'restore') await archiveFolder(folder.id, false);
            onDone();
        } catch (e) {
            setError(friendlyError(e, 'Could not save the folder. Please try again.'));
            setBusy(false);
        }
    };

    const label = { new: 'Create folder', rename: 'Save name', move: 'Move here', archive: 'Archive folder', restore: 'Restore folder' }[mode];
    return (
        <Modal title={TITLES[mode]} onClose={busy ? () => {} : onClose}
            footer={<>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant={mode === 'archive' ? 'danger' : 'primary'} onClick={save} disabled={busy}>{busy ? 'Saving…' : label}</Button>
            </>}>
            {needsName && (
                <div>
                    <FieldLabel htmlFor="sop-folder-name" required>Folder name</FieldLabel>
                    <input id="sop-folder-name" className={inputClass} value={name} maxLength={LIMITS.FOLDER_NAME_MAX + 20} autoFocus
                        onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
                </div>
            )}
            {(mode === 'new' || mode === 'move') && parents.length > 0 && (
                <div>
                    <FieldLabel htmlFor="sop-folder-parent" required hint="Folders can go up to 5 levels deep.">{mode === 'new' ? 'Put it in' : `Move “${folder?.name}” to`}</FieldLabel>
                    <select id="sop-folder-parent" className={inputClass} value={parentId || ''} onChange={(e) => setParentId(e.target.value || null)}>
                        {parents.map((p) => <option key={p.id || 'top'} value={p.id || ''}>{indent(p.depth)}{p.label}</option>)}
                    </select>
                </div>
            )}
            {mode === 'archive' && (
                <p className="text-sm text-gray-700">Archive <strong>{folder?.name}</strong>? It must be empty first — move or archive its SOPs and sub-folders. You can restore it later from <strong>Archived</strong>.</p>
            )}
            {mode === 'restore' && (
                <p className="text-sm text-gray-700">Restore <strong>{folder?.name}</strong>? It goes back where it was. If the folder it was in is archived, restore that one first.</p>
            )}
            {error && <Notice tone="error">{error}</Notice>}
        </Modal>
    );
}
